// Typed API client for the dashboard. Thin fetch wrapper that injects the bearer token,
// parses JSON, and surfaces structured error bodies (e.g. the mid-drawdown 409 warning).

import type {
  AlertEvent,
  AlertLevelInput,
  AuditEntry,
  BrokerageAccountSummary,
  PortfolioAssumptions,
  PortfolioBenchmarkInput,
  PortfolioBenchmarks,
  AccountHistory,
  ChangePreview,
  DrawdownState,
  EtfCategoryComparison,
  LadderPreview,
  ManualAccountOverride,
  MarginRateEstimate,
  MarketTarget,
  NotificationSettings,
  PolicyResult,
  PolicyVersion,
  SecuritySnapshot,
  UserPreferences,
  WatchlistComparison,
  WatchlistItem,
} from "../types";
import type { SavedMarginPlan } from "../lib/marginPlanStorage";

export class ApiClientError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

/**
 * True when an error is the backend's step-up challenge: a sensitive action (link
 * brokerage, delete account) requires a fresh authentication. The caller should prompt the
 * user to re-authenticate (password + MFA), then retry the action.
 */
export function isStepUpRequired(err: unknown): boolean {
  return (
    err instanceof ApiClientError &&
    err.status === 401 &&
    typeof err.body === "object" &&
    err.body !== null &&
    (err.body as { code?: unknown }).code === "step_up_required"
  );
}

// Resolves a currently-valid bearer token, refreshing it when expired. Async so the
// implementation can mint a fresh id token from the Cognito refresh token per request.
type TokenProvider = () => Promise<string | null>;

// Called when a request is rejected as unauthenticated and the session cannot be
// refreshed (e.g. the refresh token has expired or been revoked). The app uses this to
// sign out, which drops it back to the login screen.
type AuthFailureHandler = () => void;

export class ApiClient {
  private readonly baseUrl: string;
  private readonly getToken: TokenProvider;
  private readonly onAuthFailure?: AuthFailureHandler;

  constructor(baseUrl: string, getToken: TokenProvider, onAuthFailure?: AuthFailureHandler) {
    this.baseUrl = baseUrl.replace(/\/$/, "");
    this.getToken = getToken;
    this.onAuthFailure = onAuthFailure;
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    // Resolve (and if needed refresh) the token right before the call, so an expired
    // id token is silently replaced instead of producing a 401.
    const token = await this.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;

    const resp = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });

    const text = await resp.text();
    const parsed: unknown = text ? JSON.parse(text) : null;

    if (!resp.ok) {
      const message =
        parsed && typeof parsed === "object" && "error" in parsed
          ? String((parsed as { error: unknown }).error)
          : `request failed with ${resp.status}`;
      const error = new ApiClientError(resp.status, parsed, message);
      // A plain 401 (not the step-up challenge) means the session is no longer accepted
      // and could not be refreshed; hand off to the app to sign out and show login.
      if (resp.status === 401 && !isStepUpRequired(error)) {
        this.onAuthFailure?.();
      }
      throw error;
    }
    return parsed as T;
  }

  // --- Market targets ---
  listMarketTargets() {
    return this.request<MarketTarget[]>("GET", "/market-targets");
  }
  createMarketTarget(input: { symbol: string; price_basis?: string }) {
    return this.request<MarketTarget>("POST", "/market-targets", input);
  }
  getMarketTarget(id: string) {
    return this.request<MarketTarget>("GET", `/market-targets/${id}`);
  }
  updateMarketTarget(id: string, input: Partial<MarketTarget>) {
    return this.request<MarketTarget>("PUT", `/market-targets/${id}`, input);
  }
  validateSymbol(id: string, input: { symbol: string; price_basis?: string }) {
    return this.request<SecuritySnapshot>("POST", `/market-targets/${id}/validate`, input);
  }
  getState(id: string) {
    return this.request<DrawdownState>("GET", `/market-targets/${id}/state`);
  }
  getHistory(id: string) {
    return this.request<{ symbol: string; price_basis: string; points: unknown[] }>(
      "GET",
      `/market-targets/${id}/history`,
    );
  }
  changeTarget(
    id: string,
    input: {
      symbol?: string;
      price_basis?: string;
      confirm?: boolean;
      account_reference_value?: number;
      reason?: string;
    },
  ) {
    return this.request<ChangePreview | Record<string, unknown>>(
      "POST",
      `/market-targets/${id}/change`,
      input,
    );
  }

  // --- Policies / ladder ---
  listPolicies(id: string) {
    return this.request<PolicyVersion[]>("GET", `/market-targets/${id}/policies`);
  }
  getPolicy(id: string, version: number) {
    return this.request<PolicyVersion>("GET", `/market-targets/${id}/policies/${version}`);
  }
  createDraftPolicy(id: string, input: { alert_levels: AlertLevelInput[] }) {
    return this.request<PolicyResult>("POST", `/market-targets/${id}/policies`, input);
  }
  activatePolicy(id: string, version: number, input: { confirm?: boolean; reason?: string }) {
    return this.request<PolicyResult>(
      "POST",
      `/market-targets/${id}/policies/${version}/activate`,
      input,
    );
  }
  previewLadder(id: string, input?: { alert_levels: AlertLevelInput[] }) {
    return this.request<LadderPreview>("POST", `/market-targets/${id}/preview`, input ?? {});
  }
  restoreDefaultLadder(id: string) {
    return this.request<PolicyVersion>("POST", `/market-targets/${id}/restore-default`);
  }

  // --- Alerts / audit ---
  listAlerts(id: string) {
    return this.request<AlertEvent[]>("GET", `/market-targets/${id}/alerts`);
  }
  acknowledgeAlert(id: string, alertId: string) {
    return this.request<AlertEvent>(
      "POST",
      `/market-targets/${id}/alerts/${alertId}/acknowledge`,
    );
  }
  listAudit(id: string) {
    return this.request<AuditEntry[]>("GET", `/market-targets/${id}/audit`);
  }

  // --- Notifications ---
  getNotificationSettings() {
    return this.request<NotificationSettings>("GET", "/settings/notifications");
  }
  updateNotificationSettings(input: Partial<NotificationSettings>) {
    return this.request<NotificationSettings>("PUT", "/settings/notifications", input);
  }
  testNotifications() {
    return this.request<{ sent: boolean }>("POST", "/settings/notifications/test");
  }
  createTelegramLinkToken() {
    return this.request<{ url: string }>("POST", "/settings/telegram/link-token");
  }

  getUserPreferences() {
    return this.request<UserPreferences>("GET", "/settings/preferences");
  }
  updateUserPreferences(input: UserPreferences) {
    return this.request<UserPreferences>("PUT", "/settings/preferences", input);
  }

  // --- Plaid ---
  createLinkToken() {
    return this.request<{ link_token: string }>("POST", "/plaid/link-token");
  }
  exchangePlaidToken(publicToken: string) {
    return this.request<{ item_id: string; institution_name?: string; linked: boolean }>(
      "POST",
      "/plaid/exchange",
      { public_token: publicToken },
    );
  }
  getBrokerageAccounts() {
    return this.request<BrokerageAccountSummary>("GET", "/plaid/accounts");
  }
  getBrokerageAccountHistory() {
    return this.request<AccountHistory>("GET", "/plaid/accounts/history");
  }
  getMarginRateEstimate() {
    return this.request<MarginRateEstimate>("GET", "/plaid/margin-rate-estimate");
  }
  getPortfolioAssumptions(positions: Array<{ symbol: string; weight: number }>) {
    return this.request<PortfolioAssumptions>("POST", "/watchlist/portfolio-assumptions", {
      positions,
    });
  }
  getPortfolioBenchmarks(input: PortfolioBenchmarkInput) {
    return this.request<PortfolioBenchmarks>("POST", "/watchlist/portfolio-benchmarks", input);
  }

  // --- Manual account/margin override ---
  getManualAccount() {
    return this.request<ManualAccountOverride>("GET", "/settings/manual-account");
  }
  updateManualAccount(input: ManualAccountOverride) {
    return this.request<ManualAccountOverride>("PUT", "/settings/manual-account", input);
  }

  // --- Named margin-planner configurations ---
  listMarginPlans() {
    return this.request<SavedMarginPlan[]>("GET", "/margin-plans");
  }
  async putMarginPlan(plan: SavedMarginPlan) {
    try {
      return await this.request<SavedMarginPlan>("PUT", `/margin-plans/${plan.id}`, {
        name: plan.name,
        inputs: plan.inputs,
        schemaVersion: 12,
      });
    } catch (error) {
      const body = error instanceof ApiClientError && typeof error.body === "object"
        && error.body !== null
        ? error.body as { code?: unknown }
        : null;
      const schemaMismatch = error instanceof ApiClientError && (
        body?.code === "planner_schema_mismatch"
        || /unsupported planner schema version/i.test(error.message)
        || /planner version mismatch/i.test(error.message)
      );
      if (schemaMismatch) {
        throw new ApiClientError(
          error.status,
          error.body,
          "This app is newer than the planner server, so named plans cannot sync yet. Your draft is still saved on this device. Update the server to the same release as the app, then try again.",
        );
      }
      throw error;
    }
  }
  deleteMarginPlan(id: string) {
    return this.request<{ removed: string }>(
      "DELETE",
      `/margin-plans/${encodeURIComponent(id)}`,
    );
  }

  // --- Deployment metadata ---
  getVersion() {
    return this.request<{ version: string }>("GET", "/version");
  }

  // --- Watchlist (read-only price snapshots) ---
  listWatchlist() {
    return this.request<WatchlistItem[]>("GET", "/watchlist");
  }
  // Recompute price/returns/risk for every item. Rate-limited server-side: a 429 with
  // body { code: "refresh_cooldown", retry_after_seconds } means it was refreshed too
  // recently. Returns the refreshed list on success.
  refreshWatchlist() {
    return this.request<WatchlistItem[]>("POST", "/watchlist/refresh");
  }
  // Compare two securities side by side, computed live so any ticker works (not limited to
  // the watchlist). Returns a full snapshot for each.
  compareWatchlist(a: string, b: string) {
    const q = `a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`;
    return this.request<WatchlistComparison>("GET", `/watchlist/compare?${q}`);
  }
  compareEtfCategory(symbol: string) {
    const q = `symbol=${encodeURIComponent(symbol)}`;
    return this.request<EtfCategoryComparison>("GET", `/watchlist/category?${q}`);
  }
  addWatchlist(symbol: string) {
    return this.request<WatchlistItem>("POST", "/watchlist", { symbol });
  }
  removeWatchlist(symbol: string) {
    return this.request<{ removed: string }>(
      "DELETE",
      `/watchlist/${encodeURIComponent(symbol)}`,
    );
  }

  // --- Self-service MFA reset (public; no bearer required) ---
  // A locked-out user (lost authenticator) emails themselves a code, then confirms it
  // with their password to clear TOTP and re-enroll on next sign-in.
  startMfaReset(email: string) {
    return this.request<{ message: string }>("POST", "/auth/mfa-reset/start", { email });
  }
  confirmMfaReset(email: string, code: string, password: string) {
    return this.request<{ reset: boolean; message: string }>(
      "POST",
      "/auth/mfa-reset/confirm",
      { email, code, password },
    );
  }

  // --- Account deletion (irreversible self-service data deletion) ---
  deleteAccount() {
    return this.request<{
      deleted: boolean;
      items_deleted: number;
      plaid: Record<string, unknown>;
      cognito: Record<string, unknown>;
    }>("DELETE", "/account");
  }
}

export function apiBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_API_BASE as string | undefined;
  return fromEnv ?? "/api";
}
